// externals
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

// libraries
import { formatNumberAndCents, kebabCase } from '@makemydeal/dr-common-utils';
import * as dealScreenErrorActionCreators from '@makemydeal/dr-dash-store/actions/dealScreenErrorActionCreators';
import * as deskingSelectors from '@makemydeal/dr-dash-store/selectors/deskingSelectors';
import * as interactionAnalyticsActionCreators from '@makemydeal/dr-dash-store/actions/interactionAnalyticsActionCreators';
import * as offerActionCreators from '@makemydeal/dr-dash-store/actions/offerActionCreators';
import * as offerReduxActionCreators from '@makemydeal/dr-dash-store/actions/offerRedux';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import { dealLockHooks, useUPQRequiredFieldsValidation } from '../../../hooks';
import { useFeatureToggle } from '../../../utils/useFeatureToggle';
import { useFieldOverrideIndicator } from '../../../hooks/useFieldOverrideIndicator';
import type { ResidualValues } from '@makemydeal/dr-offer-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';

import type { ResidualValueType } from '@makemydeal/dr-platform-types';
import { RESIDUAL_DOLLAR, RESIDUAL_PERCENT, LEASE } from '@makemydeal/dr-platform-types';
import type { StateTree } from '@makemydeal/dr-dash-types';

// consts/enums
import type { RESIDUAL_TYPE } from '../../../constants';
import {
    ADJUSTED_RESIDUAL_LABEL,
    BASE_RESIDUAL_LABEL,
    DOLLAR_SIGN,
    GENERIC_LAST_FIELD_CHANGED_ERROR,
    PERCENT_SIGN
} from '../../../constants';

// utils
import { convertToNumber } from '../../../utils/formatUtils';
import type { DealTabs } from '@makemydeal/dr-shared-types';

export type ResidualFieldInputProps = {
    residualType: ResidualValueType;
    showDisabled?: boolean;
    residualAmount: number;
    residualPercent: number;
    updateResidualOverride: any;
    id: string;
    label: string;
    name?: string;
    groupId: string;
    groupTestId: string;
    valueTestId: string;
    dollarTestId: string;
    percentTestId: string;
    dollarOverrideAction: string;
    percentOverrideAction: string;
};

export type ResidualFieldConfig = {
    inputProps: ResidualFieldInputProps;
    fieldType: RESIDUAL_TYPE;
    tab?: DealTabs;
};

const OVERRIDE_INDICATOR_TEST_ID: Record<RESIDUAL_TYPE, string> = {
    baseResidual: 'base-residual-override-indicator',
    adjustedResidual: 'adjusted-residual-override-indicator'
};

enum ResidualActions {
    NO_ACTION,
    OVERRIDE_WITH_PAYMENT,
    OVERRIDE_WITHOUT_PAYMENT,
    CLEAR_OVERRIDE_WITH_PAYMENT
}

export const useResidualField = ({ inputProps, fieldType, tab }: ResidualFieldConfig) => {
    const dispatch = useDispatch();
    const overrideIndicator = useFieldOverrideIndicator(fieldType);
    const months = useSelector(offerReduxSelectors.getTermLength) as number;
    const failedDollar = useSelector(deskingSelectors.wasTheLastFailure(inputProps.dollarOverrideAction));
    const failedPercent = useSelector(deskingSelectors.wasTheLastFailure(inputProps.percentOverrideAction));
    const isUserProgramQuotes = useSelector(offerReduxSelectors.getIsUserProgramQuotes);
    const isDealScreenRedDotErrorsEnabled = useSelector(featureToggleSelectors.isDealScreenRedDotErrorsEnabled);
    const isAspPaymentGridRedesignEnabled = useSelector(featureToggleSelectors.isAspPaymentGridRedesignEnabled);
    const isExplicitLenderP1Enabled = useSelector(featureToggleSelectors.isExplicitLenderP1Enabled);
    const isImprovedAutoSelectEnabled = useSelector(featureToggleSelectors.isImprovedAutoSelectEnabled);

    const isPaymentError = useSelector(offerReduxSelectors.getIsError);
    const [isValidationError, setValidationError] = useState(false);
    const isCalculatingPayment = useSelector(offerReduxSelectors.isCalculatingPayment);
    const isLocked = dealLockHooks.useIsDealLocked();
    const { missingFields, hasMissingRequiredFields, lastPaymentMissingUPQFields } = useUPQRequiredFieldsValidation(LEASE);
    const isPaymentGridOpen = useSelector((state: StateTree) => offerReduxSelectors.getPaymentGridToggle(state, LEASE));
    const isReadOnlyForOpenGrid = isExplicitLenderP1Enabled && isPaymentGridOpen;

    const isResidualMissing = useMemo(
        () => hasMissingRequiredFields && fieldType === 'baseResidual' && missingFields.some((f) => f.field === 'residual'),
        [hasMissingRequiredFields, fieldType, missingFields]
    );

    // Used/aged vehicles often return no residual from pricing; let the dealer enter it manually
    // instead of leaving the field disabled with nothing to work from.
    const isLeaseResidualTreatmentEnabled = useFeatureToggle('enableLeaseResidualTreatment_F286227');
    const showAsManualInput = isLeaseResidualTreatmentEnabled && isResidualMissing;
    const isFieldDisabled = isLocked || (inputProps.showDisabled && !showAsManualInput);

    // Scoped to base residual: Adjusted Residual also routes through this component while
    // `enableLeaseResidualTreatment_F286227` is off.
    const suppressUnitToggleOverride = isExplicitLenderP1Enabled && fieldType === 'baseResidual';

    const mounted = useRef(false);

    const [unitType, setUnitType] = useState(inputProps.residualType);

    const isUnitTypePercentage = useMemo((): boolean => {
        return unitType === RESIDUAL_PERCENT;
    }, [unitType]);

    const getUnitTypeValue = useMemo((): number => {
        return isUnitTypePercentage ? inputProps.residualPercent : inputProps.residualAmount;
    }, [isUnitTypePercentage, inputProps.residualAmount, inputProps.residualPercent]);

    const getPrefixByType = useCallback(
        (percent?: boolean): string => {
            if (percent == null) {
                return isUnitTypePercentage ? PERCENT_SIGN : DOLLAR_SIGN;
            }
            return percent ? PERCENT_SIGN : DOLLAR_SIGN;
        },
        [isUnitTypePercentage]
    );

    const getPlaceholdeByType = useMemo((): string => {
        return isUnitTypePercentage ? '0.00000' : '0.00';
    }, [isUnitTypePercentage]);

    const getFormatted = useCallback(
        (value: number): string => {
            return isUnitTypePercentage ? String(value) : formatNumberAndCents(value);
        },
        [isUnitTypePercentage]
    );

    const [unitValue, setUnitValue] = useState({ value: getUnitTypeValue, action: ResidualActions.NO_ACTION });

    const exceedsPercentageLimit = useMemo(() => {
        return isUnitTypePercentage && unitValue.value > 100;
    }, [isUnitTypePercentage, unitValue.value]);

    // NOTE: due to the debouncing, etc this function isn't getting coverage credit, so ignore...
    const actionCaller = useCallback(
        (residualValues: ResidualValues, makePaymentCall: boolean): void => {
            const finalMakePaymentCall = makePaymentCall && !exceedsPercentageLimit;

            // we're only going to dispatch one action when the value itself is changed. But the type will persist alongside the value.
            dispatch(inputProps.updateResidualOverride(residualValues, finalMakePaymentCall));
        },
        [dispatch, inputProps, exceedsPercentageLimit]
    );

    const valueBeforeBlur = useRef(unitValue.value);
    const isBlurInProgress = useRef(false);
    const userTyped = useRef(false);
    const blocksEmptyBaseResidual = isImprovedAutoSelectEnabled && fieldType === 'baseResidual';

    const reducer = useCallback(
        (actionType: ResidualActions) => {
            switch (actionType) {
                case ResidualActions.OVERRIDE_WITH_PAYMENT:
                    // override value with payment call
                    unitValue.action = ResidualActions.NO_ACTION;
                    actionCaller({ value: unitValue.value, months, unitType }, true);
                    break;

                case ResidualActions.OVERRIDE_WITHOUT_PAYMENT:
                    // override value w/o payment call
                    unitValue.action = ResidualActions.NO_ACTION;
                    actionCaller({ value: unitValue.value, months, unitType }, false);
                    break;

                case ResidualActions.CLEAR_OVERRIDE_WITH_PAYMENT:
                    // clear override with payment call
                    unitValue.action = ResidualActions.NO_ACTION;
                    if (!isUserProgramQuotes) {
                        actionCaller({ value: undefined, months, unitType }, true);
                    }
                    break;

                default:
                    break;
            }
        },
        [unitType, unitValue.value, unitValue.action]
    );

    useEffect(() => {
        if (!mounted.current) {
            return;
        }
        const newValue = getUnitTypeValue;
        setUnitValue({ value: newValue, action: ResidualActions.NO_ACTION });
        valueBeforeBlur.current = newValue;
    }, [inputProps.residualAmount, inputProps.residualPercent]);

    useEffect(() => {
        if (!mounted.current) {
            return;
        }
        if (!isCalculatingPayment) {
            const newValue = getUnitTypeValue;
            setUnitValue({ value: newValue, action: ResidualActions.NO_ACTION });
            valueBeforeBlur.current = newValue;
            setValidationError(false);
        }
    }, [isCalculatingPayment, isPaymentError]);

    useEffect(() => {
        if (!mounted.current) {
            // add default baseResidualValue override if UPQ
            if (isUserProgramQuotes && !isReadOnlyForOpenGrid) {
                setUnitValue({ value: unitValue.value, action: ResidualActions.OVERRIDE_WITHOUT_PAYMENT });
                valueBeforeBlur.current = unitValue.value;
            }
            return;
        }

        const newValue = getUnitTypeValue;
        setUnitValue({
            value: newValue,
            action: suppressUnitToggleOverride ? ResidualActions.NO_ACTION : ResidualActions.OVERRIDE_WITHOUT_PAYMENT
        });
        valueBeforeBlur.current = newValue;

        if (suppressUnitToggleOverride && !isReadOnlyForOpenGrid) {
            dispatch(offerReduxActionCreators.updateResidualTabDisplayType(unitType, false));
        }
    }, [unitType]);

    const readOnlyResidualTypeBaseline = useRef<ResidualValueType | null>(null);

    useEffect(() => {
        if (!isReadOnlyForOpenGrid) {
            readOnlyResidualTypeBaseline.current = null;
            return;
        }
        if (readOnlyResidualTypeBaseline.current === null) {
            readOnlyResidualTypeBaseline.current = inputProps.residualType;
            return;
        }
        if (inputProps.residualType !== readOnlyResidualTypeBaseline.current) {
            readOnlyResidualTypeBaseline.current = inputProps.residualType;
            setUnitType(inputProps.residualType);
        }
    }, [isReadOnlyForOpenGrid, inputProps.residualType]);

    useEffect(() => {
        if (!mounted.current) {
            mounted.current = true;
            return;
        }
        reducer(unitValue.action);
    }, [unitValue.value, unitValue.action]);

    useEffect(() => {
        if (!isUserProgramQuotes) return;
        if (isReadOnlyForOpenGrid) return;

        const replacesClearedResidual = blocksEmptyBaseResidual && !unitValue.value;
        if (isAspPaymentGridRedesignEnabled || replacesClearedResidual) {
            // Seed the override from current offer state rather than this field's local copy. Entering a
            // residual in the payment grid flips the lease onto UPQ, which runs this effect — re-asserting
            // the stale local value here overwrote the residual that had just been typed, so the payment
            // went out with the new number while every field kept showing the old one.
            const newValue = getUnitTypeValue;
            setUnitValue({ value: newValue, action: ResidualActions.OVERRIDE_WITHOUT_PAYMENT });
            valueBeforeBlur.current = newValue;
            return;
        }

        reducer(ResidualActions.OVERRIDE_WITHOUT_PAYMENT);
    }, [isUserProgramQuotes]);

    const handleChangeUnitType = (newType: ResidualValueType): void => {
        setUnitType(newType);
    };

    const getAction = (strValue: string) => {
        const numValue = convertToNumber(strValue);
        const value = strValue.length ? numValue : 0;
        const emptyAction = isUserProgramQuotes
            ? ResidualActions.OVERRIDE_WITH_PAYMENT
            : ResidualActions.CLEAR_OVERRIDE_WITH_PAYMENT;
        const action = strValue.length ? ResidualActions.OVERRIDE_WITH_PAYMENT : emptyAction;
        return { value, action };
    };

    const restoreValueAndClearPendingPayment = () => {
        userTyped.current = false;
        dispatch(offerActionCreators.updatePaymentCallPendingState(false));
        setUnitValue({ value: valueBeforeBlur.current, action: ResidualActions.NO_ACTION });
    };

    const commitChanges = () => {
        if (unitValue.value !== valueBeforeBlur.current) {
            if (blocksEmptyBaseResidual && !unitValue.value) {
                restoreValueAndClearPendingPayment();
                return;
            }
            const { value, action } = getAction(unitValue.value ? getFormatted(unitValue.value) : '');
            valueBeforeBlur.current = value;
            setUnitValue({ value, action });
            if (userTyped.current) {
                userTyped.current = false;
                // Blanking the field removes the override, so there is nothing for the indicator to describe.
                if (action !== ResidualActions.CLEAR_OVERRIDE_WITH_PAYMENT) overrideIndicator.recordManualOverride();
            }
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            commitChanges();
        }
    };

    const handleBlur = () => {
        isBlurInProgress.current = true;
        commitChanges();
        if (fieldType === 'baseResidual') dispatch(interactionAnalyticsActionCreators.baseResidualTextInput());
        else if (fieldType === 'adjustedResidual') dispatch(interactionAnalyticsActionCreators.adjustedResidualTextInput());
    };

    const handleTextChanged = (e: any) => {
        const { value } = getAction(e.target.value);

        if (!e.isValid) {
            setValidationError(!e.isValid);
        }
        if (isBlurInProgress.current) {
            isBlurInProgress.current = false;
            return;
        }

        userTyped.current = true;
        dispatch(offerActionCreators.updatePaymentCallPendingState(value !== valueBeforeBlur.current));
        setUnitValue({ value, action: ResidualActions.NO_ACTION });
        return;
    };

    const handleDollarButtonClick = () => {
        if (fieldType === 'baseResidual') dispatch(interactionAnalyticsActionCreators.baseResidualDollarsButton());
        else if (fieldType === 'adjustedResidual') dispatch(interactionAnalyticsActionCreators.adjustedResidualDollarsButton());
    };

    const handlePercentButtonClick = () => {
        if (fieldType === 'baseResidual') dispatch(interactionAnalyticsActionCreators.baseResidualPercentButton());
        else if (fieldType === 'adjustedResidual') dispatch(interactionAnalyticsActionCreators.adjustedResidualPercentButton());
    };

    const failedMessage = useMemo(() => {
        if (isResidualMissing && (failedPercent || failedDollar || lastPaymentMissingUPQFields)) {
            return `${inputProps.label} is required`;
        }
        if (failedPercent || failedDollar) return GENERIC_LAST_FIELD_CHANGED_ERROR;

        if (exceedsPercentageLimit) return 'Percentage cannot be greater than 100';

        return undefined;
    }, [failedPercent, failedDollar, exceedsPercentageLimit, isResidualMissing, lastPaymentMissingUPQFields, inputProps.label]);

    useEffect(() => {
        if (isDealScreenRedDotErrorsEnabled && tab) {
            if (fieldType === 'baseResidual') {
                dispatch(
                    dealScreenErrorActionCreators.setFieldError(
                        tab,
                        kebabCase(BASE_RESIDUAL_LABEL),
                        Boolean(failedMessage || isValidationError || isResidualMissing)
                    )
                );
            } else if (fieldType === 'adjustedResidual') {
                dispatch(
                    dealScreenErrorActionCreators.setFieldError(
                        tab,
                        kebabCase(ADJUSTED_RESIDUAL_LABEL),
                        Boolean(failedMessage || isValidationError)
                    )
                );
            }
        }

        return () => {
            if (isDealScreenRedDotErrorsEnabled && tab) {
                const fieldName = fieldType === 'baseResidual' ? BASE_RESIDUAL_LABEL : ADJUSTED_RESIDUAL_LABEL;
                dispatch(dealScreenErrorActionCreators.setFieldError(tab, kebabCase(fieldName), false));
            }
        };
    }, [isDealScreenRedDotErrorsEnabled, dispatch, failedMessage, isValidationError, tab, fieldType]);

    return {
        label: inputProps.label,
        isReadOnlyForOpenGrid,
        overrideIndicatorProps: {
            enabled: overrideIndicator.enabled,
            show: overrideIndicator.show,
            message: overrideIndicator.message,
            testId: OVERRIDE_INDICATOR_TEST_ID[fieldType]
        },
        valueInputProps: {
            id: inputProps.id,
            name: inputProps.name ?? kebabCase(inputProps.label),
            value: unitValue.value ? getFormatted(unitValue.value) : '',
            disabled: isFieldDisabled,
            onChange: handleTextChanged,
            onBlur: handleBlur,
            onKeyDown: handleKeyDown,
            hasError: Boolean(failedMessage),
            placeholder: getPlaceholdeByType,
            inputPrefix: getPrefixByType(),
            'data-testid': inputProps.valueTestId,
            errorMessage: failedMessage,
            allowDecimal: true,
            decimalMaxLength: 5,
            decimalMinLength: 0,
            autoInsertCommas: true,
            size: 'sm' as const,
            maxValue: 9999999,
            readOnly: isReadOnlyForOpenGrid
        },
        unitToggleProps: {
            size: 'sm' as const,
            id: inputProps.groupId,
            'data-testid': inputProps.groupTestId,
            toggleButtonOptions: [
                {
                    id: 'dollarToggle',
                    value: getPrefixByType(false),
                    children: <span>{getPrefixByType(false)}</span>,
                    selected: !isUnitTypePercentage,
                    'data-testid': inputProps.dollarTestId,
                    ...(!isLocked && {
                        onClick: (_e: any) => {
                            handleChangeUnitType(RESIDUAL_DOLLAR);
                            handleDollarButtonClick();
                        }
                    })
                },
                {
                    id: 'percentToggle',
                    value: getPrefixByType(true),
                    children: <span>{getPrefixByType(true)}</span>,
                    selected: isUnitTypePercentage,
                    'data-testid': inputProps.percentTestId,
                    ...(!isLocked && {
                        onClick: (_e: any) => {
                            handleChangeUnitType(RESIDUAL_PERCENT);
                            handlePercentButtonClick();
                        }
                    })
                }
            ],
            toggleWidth: '60px'
        }
    };
};
